import { Box, Button, Card, Grid, Stack, Typography, formatPhoneNumber, useTheme } from "@compass/core";
import { Input } from "components";
import { SendMessageRequestDto, TextingChatHubAction, TextingConversation, TextingMessage, TextingOptInDto } from "generated/openApi";
import produce from "immer";
import { ConnectionStatus } from "pages/texting/ConnectionStatus";
import { useTextingChatHubConnection } from "pages/texting/useTextingChatHubConnection";
import { useEffect, useRef, useState } from "react";
import ApiService from "services/apiService";
import { TextingMessageBubble } from "./TextingMessageBubble";

export const ChatScreen = (props: { conversationId: number | undefined }) => {
  const theme = useTheme();
  const [conversation, setConversation] = useState<TextingConversation>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState<string>("");
  const [optedInNumbers, setOptedInNumbers] = useState<TextingOptInDto[]>();

  const { textingChatHubConnection, connectionStatus } = useTextingChatHubConnection({
    conversationId: props.conversationId,
  });

  useEffect(() => {
    if (props.conversationId) {
      ApiService.textingConversations.get(props.conversationId).then((response) => {
        setConversation(response);
        scrollBottom();
      });

      ApiService.textingConversations.getOptedInStudentNumbers().then((response) => {
        setOptedInNumbers(response);
      });
    }
  }, [props.conversationId]);

  // handle incoming message
  useEffect(() => {
    if (!textingChatHubConnection) return;

    const handleIncomingMessage = (newTextingMessageJsonString: string) => {
      const newTextingMessage = JSON.parse(newTextingMessageJsonString) as TextingMessage;
      

      if (conversation && newTextingMessage.textingConversationId === conversation.id) {
        setConversation(
          produce(conversation, (draft) => {
            draft!.textingMessages!.push(newTextingMessage);
          })
        );

        // scrollBottom();
      }
    };


    textingChatHubConnection.on(TextingChatHubAction[TextingChatHubAction.BroadcastNewMessage], handleIncomingMessage);

    return () => {
      textingChatHubConnection.off(
        TextingChatHubAction[TextingChatHubAction.BroadcastNewMessage],
        handleIncomingMessage
      );
    };
  }, [conversation, textingChatHubConnection, messagesContainerRef]);

  const renderTextMessage = (textMessage: TextingMessage, index: number) => {
    return (
      <Grid item xs={12} key={`${index}:${textMessage.sid}`} sx={{ display: "flex" }}>
        <TextingMessageBubble
          message={textMessage.messageBody}
          isSender={!textMessage.inbound}
          dateSent={textMessage.dateSent}
        />
      </Grid>
    );
  };

  function scrollBottom() {
    setTimeout(() => {
      if (messagesContainerRef.current)
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }, 0);

    setTimeout(() => {
      if (messagesContainerRef.current)
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }, 500);
  }

  const markAsRead = () => {
    console.log("marking as read...");
  };

  const handleArchive = () => {
    console.log("archiving...");
  };

  const sendMessage = () => {
    const request: SendMessageRequestDto = {
      conversationId: props.conversationId!,
      messageBody: message,
    };

    ApiService.textingConversations.sendMessage(request).then((sendResponse) => {
      setMessage("");
      ApiService.textingConversations.get(props.conversationId).then((response) => {
        setConversation(response);
        scrollBottom();
      });
    });
  };

  const header = () => {
    return (
      <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
        <Stack>
          {/* <Typography variant="h6">{conversation?.id}</Typography> */}
          <Typography variant="h6">
            {conversation?.person?.fullName} - {conversation?.personId} ({conversation?.personType?.name})
          </Typography>
          <Typography variant="body1">{formatPhoneNumber(conversation?.destinationPhoneNumber)}</Typography>
          {/* <Typography variant="h6">
            CurrentTopic:
            {conversation?.currentTopic}
          </Typography> */}
          <Box>
            <ConnectionStatus status={connectionStatus} />
          </Box>
        </Stack>

        {/* <Stack direction="row" spacing="md" justifyContent="flex-end">
          <Button onClick={() => markAsRead()} variant="outlined">
            Mark as Unread
          </Button>
          <Button onClick={() => handleArchive()} variant="contained" color="error">
            Archive
          </Button>
        </Stack> */}
      </Stack>
    );
  };

  const messagesContainer = () => {
    return (
      <Stack overflow="auto">{conversation?.textingMessages?.map((m, index) => renderTextMessage(m, index))}</Stack>
    );
  };

  const sendTextContainer = () => {
    const canSendMessage = optedInNumbers?.some(n => n.phoneNumber == conversation?.destinationPhoneNumber) && message && message.trim().length > 0;

    return (
      <Stack direction="row">
        <Input multiline value={message} onChange={(e) => setMessage(e.target.value)} />

        <Button variant="contained" onClick={sendMessage} disabled={!canSendMessage}>
          Send
        </Button>
      </Stack>
    );
  };

  return (
    <Card style={{ height: "100%" }} bodySx={{ p: 0 }}>
      <Stack height="100%">
        <Box borderBottom={`1px solid ${theme.palette.border.main}`} p={4}>
          {header()}
        </Box>

        <Box
          ref={messagesContainerRef}
          borderBottom={`1px solid ${theme.palette.border.main}`}
          flexGrow={1}
          p={4}
          overflow="auto"
        >
          {messagesContainer()}
        </Box>

        <Box>{sendTextContainer()}</Box>
      </Stack>
    </Card>
  );
};
